import React, { useEffect, useMemo, useState } from 'react';
import { getSignals, getMissLog, getDeviations, getRiskLog, getTrades, getHealth, getTradovateHealth } from '../api';
import { ClassBadge, EntryModeBadge, QualityScore, TradovatePill, SmtChip, FormationSpeedPill, HtfDistancePill, categorizeTradovate } from './ClassBadge';
import { CheckCircle2, AlertTriangle, XCircle, Inbox, Loader2 } from 'lucide-react';
import { formatTradeTime } from '../utils/time';
import { pnlColorClass } from '../utils/pnl';
import { PageHeader } from './ui/PageHeader';
import { KpiCard } from './ui/KpiCard';
import { EmptyState } from './ui/EmptyState';
import { TableWrapper, Thead, Th, Tbody, Tr, Td } from './ui/TableWrapper';
import { InstrumentCell } from './ui/InstrumentCell';
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip as RTooltip } from 'recharts';
// TECH DEBT: TradesCharts is owned by Trades.tsx. When the Trades nav item is
// removed (separate task), TradesCharts must be relocated (e.g. to a shared
// charts module or into Signals.tsx) before Trades.tsx can be deleted.
import { TradesCharts } from './Trades';
import ShadowObservers from './ShadowObservers';

const DEFAULT_COMMISSION_PER_RT = 5.82;

const toneForPnl = (v: number): 'positive' | 'negative' | 'neutral' =>
  v > 0 ? 'positive' : v < 0 ? 'negative' : 'neutral';

const formatDuration = (seconds: number) => {
  if (!seconds) return '--';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

type SignalsTab = 'all' | 'misses' | 'deviations' | 'risk-events' | 'shadow';

const TABS: { id: SignalsTab; label: string }[] = [
  { id: 'all', label: 'All Signals' },
  { id: 'misses', label: 'Misses' },
  { id: 'deviations', label: 'Deviations' },
  { id: 'risk-events', label: 'Risk Events' },
  { id: 'shadow', label: 'Shadow Observers' },
];

const PAGE_SIZE_KEY = 'signals_page_size';
const PAGE_SIZES = [25, 50, 100];

const HIDE_TEST_KEY = 'signals_hide_test';

const smtStrength = (s: any, field: 'smt_strength_15m' | 'smt_strength_1m') =>
  (s?.[field] ?? s?.raw_payload?.[field] ?? 'none') as string;

const ScoreHeaderTooltip = () => (
  <span className="relative group cursor-help inline-flex items-center gap-1">
    Score <span className="text-gray-500 text-xs">ⓘ</span>
    <span className="hidden group-hover:block absolute z-20 bg-gray-800 text-xs text-white p-2 rounded w-48 top-6 left-0 whitespace-pre-line normal-case">
      75+ SMR confirmed{'\n'}
      50+ Macro + Liquidity{'\n'}
      30+ Macro only{'\n'}
      5+ Breaker only
    </span>
  </span>
);

// Criteria-based model (Phase B, 2026-07-10): 4 boolean gate criteria, in order.
const CRITERIA_LABELS: { key: string; label: string }[] = [
  { key: 'htf_liquidity_taken', label: 'HTF Liquidity Taken' },
  { key: 'smt_confirmed', label: 'SMT Confirmed' },
  { key: 'breaker_formed', label: 'Breaker Formed' },
  { key: 'in_macro', label: 'In Macro Window' },
];

const CriteriaHeaderTooltip = () => (
  <span className="relative group cursor-help inline-flex items-center gap-1">
    Criteria <span className="text-gray-500 text-xs">ⓘ</span>
    <span className="hidden group-hover:block absolute z-20 bg-gray-800 text-xs text-white p-2 rounded w-52 top-6 left-0 whitespace-normal normal-case">
      {CRITERIA_LABELS.map((c, i) => (
        <div key={c.key}>{i + 1}. {c.label}</div>
      ))}
    </span>
  </span>
);

// Renders "X/4" with a hover tooltip listing each of the 4 criteria met/missed.
function CriteriaCell({ flags, count }: { flags?: Record<string, boolean> | null; count?: number | null }) {
  if (count == null || !flags) return <span className="text-gray-600">--</span>;
  const cls =
    count === 4 ? 'bg-green-900 text-green-300' :
    count === 3 ? 'bg-blue-900 text-blue-300' :
    'bg-gray-700 text-gray-300';
  const pillClass = `text-xs font-medium px-2 py-0.5 rounded-full ${cls}`;
  return (
    <span className="relative group cursor-help inline-flex items-center">
      <span className={pillClass}>{count}/4</span>
      <span className="hidden group-hover:block absolute z-20 bg-gray-800 text-xs text-white p-2 rounded w-52 top-6 left-0 whitespace-normal normal-case">
        {CRITERIA_LABELS.map(c => {
          const met = !!flags[c.key];
          const markClass = met ? 'text-green-400' : 'text-gray-500';
          return (
            <div key={c.key} className="flex items-center gap-1.5">
              <span className={markClass}>{met ? '\u2713' : '\u2717'}</span>
              <span>{c.label}</span>
            </div>
          );
        })}
      </span>
    </span>
  );
}

function readTabFromUrl(): SignalsTab {
  const params = new URLSearchParams(window.location.search);
  const t = params.get('tab') as SignalsTab | null;
  if (t && TABS.some(x => x.id === t)) return t;
  return 'all';
}

function writeTabToUrl(tab: SignalsTab) {
  const params = new URLSearchParams(window.location.search);
  if (tab === 'all') params.delete('tab');
  else params.set('tab', tab);
  const qs = params.toString();
  const newUrl = `${window.location.pathname}${qs ? '?' + qs : ''}${window.location.hash}`;
  window.history.replaceState(null, '', newUrl);
}

export default function Signals() {
  const [tab, setTab] = useState<SignalsTab>(readTabFromUrl());
  const [tradesView, setTradesView] = useState<boolean>(() => {
    return localStorage.getItem('signals_view') !== 'signals';
  });

  useEffect(() => {
    writeTabToUrl(tab);
  }, [tab]);

  return (
    <div>
      <PageHeader
        title="Signals"
        description="Incoming TradingView alerts, trades taken, misses, deviations, risk events, and shadow observers."
      />

      <div className="flex gap-2 mb-4">
        <button
          onClick={() => { setTradesView(true); localStorage.setItem('signals_view', 'trades'); }}
          className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
            tradesView ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
          }`}
        >
          Trades Taken
        </button>
        <button
          onClick={() => { setTradesView(false); localStorage.setItem('signals_view', 'signals'); }}
          className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
            !tradesView ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
          }`}
        >
          All Signals
        </button>
      </div>

      {tradesView ? (
        <TradesView />
      ) : (
        <>
          <div className="flex gap-1 border-b border-gray-800 mb-6">
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  tab === t.id ? 'border-blue-500 text-white' : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'all' && <AllSignalsTab />}
          {tab === 'misses' && <MissesTab />}
          {tab === 'deviations' && <DeviationsTab />}
          {tab === 'risk-events' && <RiskEventsTab />}
          {tab === 'shadow' && <ShadowObservers />}
        </>
      )}
    </div>
  );
}

function usePagination(totalRows: number, storageKey: string) {
  const initial = (() => {
    const stored = Number(localStorage.getItem(storageKey));
    return PAGE_SIZES.includes(stored) ? stored : 50;
  })();
  const [pageSize, setPageSize] = useState<number>(initial);
  const [page, setPage] = useState<number>(1);
  useEffect(() => {
    localStorage.setItem(storageKey, String(pageSize));
    setPage(1);
  }, [pageSize, storageKey]);
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, pageCount);
  const startIdx = (safePage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, totalRows);
  return { pageSize, setPageSize, page: safePage, setPage, pageCount, startIdx, endIdx };
}

function PaginationBar({
  totalRows, startIdx, endIdx, page, pageCount, setPage, pageSize, setPageSize, noun,
}: {
  totalRows: number; startIdx: number; endIdx: number; page: number; pageCount: number;
  setPage: (n: number | ((p: number) => number)) => void; pageSize: number; setPageSize: (n: number) => void; noun: string;
}) {
  return (
    <div className="flex items-center justify-between text-xs text-gray-500 flex-wrap gap-2">
      <span>
        {totalRows === 0 ? `No ${noun}` : `Showing ${startIdx + 1}-${endIdx} of ${totalRows} ${noun}`}
      </span>
      <div className="flex items-center gap-2">
        <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
          className="bg-gray-800 text-xs text-gray-300 px-2 py-1 rounded border border-gray-700">
          {PAGE_SIZES.map(n => <option key={n} value={n}>{n} per page</option>)}
        </select>
        <button
          disabled={page <= 1}
          onClick={() => setPage((p: number) => Math.max(1, p - 1))}
          className="px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >Prev</button>
        <span>Page {page} / {pageCount}</span>
        <button
          disabled={page >= pageCount}
          onClick={() => setPage((p: number) => Math.min(pageCount, p + 1))}
          className="px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >Next</button>
      </div>
    </div>
  );
}

function DirectionBadge({ dir }: { dir?: string }) {
  if (!dir) return null;
  const cls = dir === 'long' ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300';
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>
      {dir.toUpperCase()}
    </span>
  );
}

function TableLoadingRow({ colSpan }: { colSpan: number }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-3 py-6">
        <div className="flex flex-col items-center justify-center text-gray-500 gap-2 py-6">
          <Loader2 size={24} className="animate-spin" />
          <span className="text-sm">Loading...</span>
        </div>
      </td>
    </tr>
  );
}

// ===== All Signals Tab =====
function AllSignalsTab() {
  const [signals, setSignals] = useState<any[]>([]);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(true);
  const [classFilter, setClassFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [decisionFilter, setDecisionFilter] = useState<string>('all');
  const [brokerFilter, setBrokerFilter] = useState<string>('all');
  const [smtFilter, setSmtFilter] = useState<string>('all');
  const [hideTest, setHideTest] = useState<boolean>(() => {
    const stored = localStorage.getItem(HIDE_TEST_KEY);
    return stored === null ? true : stored === 'true';
  });
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(HIDE_TEST_KEY, String(hideTest));
  }, [hideTest]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2000);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };
  const SortIndicator = ({ field }: { field: string }) => sortField === field ? <span className="text-blue-400 ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span> : null;

  const matchesBroker = (s: any) => {
    if (brokerFilter === 'all') return true;
    const kind = categorizeTradovate(!!s.tradovate_executed, s.tradovate_reason);
    if (brokerFilter === 'executed') return kind === 'live';
    if (brokerFilter === 'skipped') return kind === 'skip';
    if (brokerFilter === 'failed') return kind === 'fail';
    return true;
  };
  const matchesSmt = (s: any) => {
    if (smtFilter === 'all') return true;
    const confirmed = !!s.smt_confirmed;
    const s15 = smtStrength(s, 'smt_strength_15m');
    const s1 = smtStrength(s, 'smt_strength_1m');
    if (smtFilter === 'dual15') return confirmed && s15 === 'dual';
    if (smtFilter === 'any15') return confirmed && (s15 === 'dual' || s15 === 'es_only' || s15 === 'ym_only');
    if (smtFilter === 'any') return confirmed && [s15, s1].some(v => v === 'dual' || v === 'es_only' || v === 'ym_only');
    if (smtFilter === 'none') return !confirmed || (s15 === 'none' && s1 === 'none');
    return true;
  };

  // Fix 4: SKIP is its own decision. The system chose not to act (score gate or
  // classifier opt-out), distinct from outright rejection.
  const decisionOf = (s: any): 'triggered' | 'rejected' | 'skipped' => {
    if (s.status === 'skipped') return 'skipped';
    const reason: string = s.tradovate_reason || '';
    if (/score:|autoexecute_disabled|classifier_skip/i.test(reason)) return 'skipped';
    if (s.status === 'rejected' || s.status === 'expired' || s.status === 'cancelled') return 'rejected';
    if (s.status === 'triggered' || s.status === 'pending') return 'triggered';
    return 'triggered';
  };

  const outcomeOf = (s: any): 'filled' | 'missed' | 'closed' | 'live' | null => {
    // No outcome for skipped signals -- the system never tried.
    if (decisionOf(s) === 'skipped') return null;
    const kind = categorizeTradovate(!!s.tradovate_executed, s.tradovate_reason);
    if (kind === 'live') return 'filled';
    if (kind === 'fail') return 'missed';
    if (s.status === 'expired' || s.status === 'cancelled') return 'closed';
    if (s.status === 'pending') return 'live';
    return null;
  };

  const filteredSignals = signals.filter((s: any) => {
    if (hideTest && /^manual_test/i.test(s.alert_source || '')) return false;
    if (classFilter !== 'all' && s.signal_class !== classFilter) return false;
    if (sourceFilter !== 'all' && s.alert_source !== sourceFilter) return false;
    if (decisionFilter !== 'all' && decisionOf(s) !== decisionFilter) return false;
    if (!matchesBroker(s)) return false;
    if (!matchesSmt(s)) return false;
    return true;
  });

  const sortedSignals = useMemo(() => {
    return [...filteredSignals].sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredSignals, sortField, sortDir]);

  const { pageSize, setPageSize, page, setPage, pageCount, startIdx, endIdx } =
    usePagination(sortedSignals.length, PAGE_SIZE_KEY);
  const pageRows = sortedSignals.slice(startIdx, endIdx);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await getSignals();
        setSignals(r.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, []);

  const sources = Array.from(new Set(sortedSignals.map(s => s.alert_source).filter(Boolean)));

  return (
    <div className="space-y-3 relative">
      {toast && (
        <div className="fixed bottom-4 right-4 z-50 bg-gray-800 border border-gray-700 text-xs text-gray-200 px-3 py-2 rounded shadow-lg">
          {toast}
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-base font-semibold text-white">Signal Log</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
            <input type="checkbox" checked={hideTest} onChange={e => setHideTest(e.target.checked)} />
            Hide test signals
          </label>
          <select value={classFilter} onChange={e => setClassFilter(e.target.value)}
            className="bg-gray-800 text-xs text-gray-300 px-2 py-1 rounded border border-gray-700">
            <option value="all">All Classes</option>
            <option value="SMR">SMR</option>
            <option value="MACRO_LIQ">MACRO_LIQ</option>
            <option value="MACRO_ONLY">MACRO_ONLY</option>
            <option value="BREAKER_ONLY">BREAKER_ONLY</option>
            <option value="TREND_CONT">TREND_CONT</option>
            <option value="SECOND_CHANCE">SECOND_CHANCE</option>
          </select>
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
            className="bg-gray-800 text-xs text-gray-300 px-2 py-1 rounded border border-gray-700">
            <option value="all">All Alerts</option>
            {sources.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={decisionFilter} onChange={(e) => setDecisionFilter(e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300">
            <option value="all">All Decisions</option>
            <option value="triggered">Triggered</option>
            <option value="skipped">Skipped</option>
            <option value="rejected">Rejected</option>
          </select>
          <select value={brokerFilter} onChange={(e) => setBrokerFilter(e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300">
            <option value="all">All Broker</option>
            <option value="executed">Executed</option>
            <option value="skipped">Skipped</option>
            <option value="failed">Failed</option>
          </select>
          <select value={smtFilter} onChange={(e) => setSmtFilter(e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300">
            <option value="all">All SMT</option>
            <option value="dual15">Dual 15m</option>
            <option value="any15">Any 15m</option>
            <option value="any">Any SMT</option>
            <option value="none">No SMT</option>
          </select>
        </div>
      </div>

      <SignalsThroughput signals={signals} decisionOf={decisionOf} />

      <div className="overflow-y-auto max-h-[600px]">
        <TableWrapper>
          <Thead>
            <Th onClick={() => handleSort('created_at')}>Time <SortIndicator field="created_at" /></Th>
            <Th onClick={() => handleSort('instrument')}>Inst <SortIndicator field="instrument" /></Th>
            <Th onClick={() => handleSort('direction')}>Dir <SortIndicator field="direction" /></Th>
            <Th>Entry</Th>
            <Th>Stop</Th>
            <Th onClick={() => handleSort('signal_class')}>Class <SortIndicator field="signal_class" /></Th>
            <Th><CriteriaHeaderTooltip /></Th>
            <Th onClick={() => handleSort('quality_score')}><ScoreHeaderTooltip /> <SortIndicator field="quality_score" /></Th>
            <Th onClick={() => handleSort('alert_source')}>Alert <SortIndicator field="alert_source" /></Th>
            <Th>Decision</Th>
            <Th>Outcome</Th>
          </Thead>
          <Tbody>
            {loading ? (
              <TableLoadingRow colSpan={11} />
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-3 py-6">
                  <EmptyState icon={<Inbox size={28} />} message="No signals match filters" sub="Adjust filters or wait for new alerts." />
                </td>
              </tr>
            ) : pageRows.map((s: any) => {
              const decision = decisionOf(s);
              const outcome = outcomeOf(s);
              return (
                <Tr key={s.id}>
                  <Td className="text-gray-400 text-xs">{formatTradeTime(s.created_at)}</Td>
                  <Td className="font-medium"><InstrumentCell symbol={s.instrument} /></Td>
                  <Td><DirectionBadge dir={s.direction} /></Td>
                  <Td className="font-mono text-xs">{s.entry_price || '--'}</Td>
                  <Td className="font-mono text-xs">{s.stop_price || '--'}</Td>
                  <Td className="whitespace-nowrap">
                    <ClassBadge signalClass={s.signal_class} />
                    <EntryModeBadge mode={s.entry_mode} />
                    <SmtChip
                      confirmed={s.smt_confirmed}
                      strength15m={smtStrength(s, 'smt_strength_15m')}
                      strength1m={smtStrength(s, 'smt_strength_1m')}
                    />
                    <FormationSpeedPill signal={s} />
                    <HtfDistancePill signal={s} />
                  </Td>
                  <Td>
                    <CriteriaCell
                      flags={{
                        htf_liquidity_taken: !!s.htf_liquidity_taken,
                        smt_confirmed: !!s.smt_confirmed,
                        breaker_formed: !!s.breaker_formed,
                        in_macro: !!s.in_macro,
                      }}
                      count={[s.htf_liquidity_taken, s.smt_confirmed, s.breaker_formed, s.in_macro].filter(Boolean).length}
                    />
                  </Td>
                  <Td><QualityScore score={s.quality_score} /></Td>
                  <Td className="text-xs"><AlertCell source={s.alert_source} /></Td>
                  <Td className="whitespace-nowrap">
                    <DecisionPill decision={decision} />
                  </Td>
                  <Td className="whitespace-nowrap">
                    {outcome && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full mr-1 ${
                        outcome === 'filled' ? 'bg-green-900 text-green-300' :
                        outcome === 'missed' ? 'bg-yellow-900 text-yellow-300' :
                        outcome === 'live' ? 'bg-purple-900 text-purple-300' :
                        'bg-gray-700 text-gray-300'
                      }`}>{outcome}</span>
                    )}
                    {/* Skipped decisions never produced an outcome -- suppress broker pill noise. */}
                    {decision !== 'skipped' && (
                      <TradovatePill
                        executed={s.tradovate_executed}
                        reason={s.tradovate_reason}
                        orderId={s.tradovate_order_id}
                        onCopy={showToast}
                      />
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </TableWrapper>
      </div>

      <PaginationBar
        totalRows={sortedSignals.length}
        startIdx={startIdx} endIdx={endIdx}
        page={page} pageCount={pageCount} setPage={setPage}
        pageSize={pageSize} setPageSize={setPageSize}
        noun="signals"
      />
    </div>
  );
}

// ===== Trades View (toggle target) =====
function TradesView() {
  const [trades, setTrades] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tvHealth, setTvHealth] = useState<any>(null);

  // Mirror Trades.tsx exactly
  const [classFilter, setClassFilter] = useState<string>('all');
  const [showSystem, setShowSystem] = useState<boolean>(false);

  // Added in TradesView (no Trades.tsx counterpart)
  const [directionFilter, setDirectionFilter] = useState<'all' | 'long' | 'short'>('all');
  const [dateRange, setDateRange] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [exitStrategyFilter, setExitStrategyFilter] = useState<string>('all');
  const [pnlFilter, setPnlFilter] = useState<'all' | 'winners' | 'losers'>('all');
  const [scoreMinInput, setScoreMinInput] = useState<string>('');
  const [scoreMaxInput, setScoreMaxInput] = useState<string>('');

  // Sort state mirrors Trades.tsx
  const [sortField, setSortField] = useState<string>('open_time');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const handleSort = (f: string) => {
    if (sortField === f) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('desc'); }
  };
  const SortIndicator = ({ field }: { field: string }) =>
    sortField === field ? <span className="text-blue-400 ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span> : null;

  useEffect(() => {
    const load = async () => {
      try {
        const [t, h, tv] = await Promise.all([getTrades(), getHealth(), getTradovateHealth()]);
        if (tv?.data?.today_pnl !== undefined) setTvHealth(tv.data);
        else if (h?.data?.today_pnl !== undefined) setTvHealth(h.data);
        setTrades(t.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const isOrphan = (t: any) => t.signal_class === 'ORPHAN' || t.signal_class === 'ORPHAN_DUPLICATE';

  const inDateRange = (t: any) => {
    if (dateRange === 'all') return true;
    const raw = t.open_time || t.created_at;
    const ts = raw ? new Date(raw).getTime() : 0;
    if (!ts) return false;
    if (dateRange === 'today') {
      const sod = new Date(); sod.setHours(0, 0, 0, 0);
      return ts >= sod.getTime();
    }
    const day = 86400000;
    if (dateRange === 'week') return ts >= Date.now() - 7 * day;
    if (dateRange === 'month') return ts >= Date.now() - 30 * day;
    return true;
  };

  // KPIs/equity curve use the shared base (class + direction + date). The
  // table-only filters (exit/score/p&l) are NOT applied to KPIs -- a
  // "winners only" KPI of "100% win rate" would be meaningless.
  const filtered = trades.filter(t => {
    if (classFilter !== 'all' && t.signal_class !== classFilter) return false;
    if (directionFilter !== 'all' && t.direction !== directionFilter) return false;
    if (!inDateRange(t)) return false;
    return true;
  });

  // KPIs/equity curve always exclude orphans -- they distort P&L stats (per Trades.tsx).
  const filteredClosed = filtered.filter(t => t.status === 'closed' && !isOrphan(t));

  const totalPnl = filteredClosed.reduce((sum, t) => sum + (t.net_pnl ?? t.pnl ?? 0), 0);
  const winTrades = filteredClosed.filter(t => (t.net_pnl ?? t.pnl ?? 0) > 0);
  const lossTrades = filteredClosed.filter(t => (t.net_pnl ?? t.pnl ?? 0) < 0);
  const wins = winTrades.length;
  const losses = lossTrades.length;
  const total = filteredClosed.length;
  const winRate = total > 0 ? (wins / total * 100) : 0;
  const avgWin = wins > 0 ? winTrades.reduce((s, t) => s + (t.net_pnl ?? t.pnl ?? 0), 0) / wins : 0;
  const avgLoss = losses > 0 ? lossTrades.reduce((s, t) => s + (t.net_pnl ?? t.pnl ?? 0), 0) / losses : 0;

  const totalCommission = filteredClosed.reduce(
    (sum, t) => sum + (t.commission_usd ?? DEFAULT_COMMISSION_PER_RT * (t.quantity || 1)),
    0
  );

  // Score range w/ NaN guard: empty or invalid input => unbounded edge.
  const scoreMinParsed = parseFloat(scoreMinInput);
  const scoreMaxParsed = parseFloat(scoreMaxInput);
  const scoreMin = scoreMinInput === '' || Number.isNaN(scoreMinParsed) ? -Infinity : scoreMinParsed;
  const scoreMax = scoreMaxInput === '' || Number.isNaN(scoreMaxParsed) ? Infinity : scoreMaxParsed;
  const scoreFilterActive = scoreMin !== -Infinity || scoreMax !== Infinity;

  const pnlOf = (t: any) => t.status === 'open' ? (t.unrealized_pnl ?? 0) : (t.net_pnl ?? t.pnl ?? 0);

  // Table rows: orphan toggle + exit/score/p&l filters on top of the shared base.
  const tableRowsFiltered = filtered.filter(t => {
    if (!showSystem && isOrphan(t)) return false;
    if (exitStrategyFilter !== 'all' && t.exit_strategy !== exitStrategyFilter) return false;
    if (scoreFilterActive) {
      if (typeof t.quality_score !== 'number') return false;
      if (t.quality_score < scoreMin || t.quality_score > scoreMax) return false;
    }
    if (pnlFilter !== 'all') {
      const p = pnlOf(t);
      if (pnlFilter === 'winners' && !(p > 0)) return false;
      if (pnlFilter === 'losers' && !(p < 0)) return false;
    }
    return true;
  });

  const tableRows = useMemo(() => {
    return [...tableRowsFiltered].sort((a, b) => {
      // Fix (2026-07-15): sortField='pnl' was reading raw t.pnl directly,
      // while the P&L column itself displays pnlOf(t) (prefers net_pnl,
      // falls back to unrealized_pnl for open trades) -- sort order visibly
      // didn't match the displayed values. Route the 'pnl' field through the
      // same helper the column uses.
      const av = sortField === 'pnl' ? pnlOf(a) : a[sortField];
      const bv = sortField === 'pnl' ? pnlOf(b) : b[sortField];
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
  }, [tableRowsFiltered, sortField, sortDir]);

  const hiddenSystemCount = filtered.filter(t => isOrphan(t)).length;

  const { pageSize, setPageSize, page, setPage, pageCount, startIdx, endIdx } =
    usePagination(tableRows.length, 'signals_trades_page_size');
  const pageRows = tableRows.slice(startIdx, endIdx);

  const winRateTone: 'positive' | 'negative' | 'neutral' | 'default' =
    total === 0 ? 'neutral' : winRate >= 55 ? 'positive' : winRate >= 40 ? 'default' : 'negative';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label="Today P&L"
          value={tvHealth?.today_pnl != null ? `${tvHealth.today_pnl > 0 ? '+' : ''}$${Number(tvHealth.today_pnl).toFixed(2)}` : `${totalPnl > 0 ? '+' : ''}$${totalPnl.toFixed(2)}`}
          tone={toneForPnl(tvHealth?.today_pnl ?? totalPnl)}
          sub={
            <>
              <span>{tvHealth?.today_pnl != null ? `Tradovate · SOD $${Number(tvHealth.sod_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `${total} closed trades`}</span>
              {tvHealth?.today_pnl == null && <span className="block text-gray-400">Commission: -${totalCommission.toFixed(2)}</span>}
            </>
          }
        />
        <KpiCard
          label="Win Rate"
          value={total > 0 ? `${winRate.toFixed(1)}%` : '--'}
          tone={winRateTone}
          sub={`${wins}W / ${losses}L`}
        />
        <KpiCard
          label="Avg Win"
          value={wins > 0 ? `$${avgWin.toFixed(2)}` : '--'}
          tone={wins > 0 ? 'positive' : 'neutral'}
        />
        <KpiCard
          label="Avg Loss"
          value={losses > 0 ? `$${avgLoss.toFixed(2)}` : '--'}
          tone={losses > 0 ? 'negative' : 'neutral'}
        />
      </div>

      <TradesCharts trades={filteredClosed} />

      <div className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-base font-semibold text-white">Trades Taken</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <label className={`flex items-center gap-1.5 text-xs cursor-pointer ${showSystem ? 'text-gray-300' : 'text-gray-500'}`}>
              <input
                type="checkbox"
                checked={showSystem}
                onChange={e => { setShowSystem(e.target.checked); setPage(1); }}
              />
              Show system trades
            </label>
            <select value={classFilter} onChange={e => { setClassFilter(e.target.value); setPage(1); }}
              className="bg-gray-800 text-xs text-gray-300 px-2 py-1 rounded border border-gray-700">
              <option value="all">All Classes</option>
              <option value="SMR">SMR</option>
              <option value="MACRO_LIQ">MACRO_LIQ</option>
              <option value="MACRO_ONLY">MACRO_ONLY</option>
              <option value="TREND_CONT">TREND_CONT</option>
              <option value="SECOND_CHANCE">SECOND_CHANCE</option>
              <option value="ORPHAN">ORPHAN</option>
            </select>
            <select value={directionFilter} onChange={e => { setDirectionFilter(e.target.value as 'all' | 'long' | 'short'); setPage(1); }}
              className="bg-gray-800 text-xs text-gray-300 px-2 py-1 rounded border border-gray-700">
              <option value="all">All Directions</option>
              <option value="long">Long</option>
              <option value="short">Short</option>
            </select>
            <select value={dateRange} onChange={e => { setDateRange(e.target.value as 'all' | 'today' | 'week' | 'month'); setPage(1); }}
              className="bg-gray-800 text-xs text-gray-300 px-2 py-1 rounded border border-gray-700">
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 days</option>
              <option value="month">Last 30 days</option>
            </select>
            <select value={exitStrategyFilter} onChange={e => { setExitStrategyFilter(e.target.value); setPage(1); }}
              className="bg-gray-800 text-xs text-gray-300 px-2 py-1 rounded border border-gray-700">
              <option value="all">All Exits</option>
              <option value="scaled_target">scaled_target</option>
              <option value="scaled">scaled</option>
              <option value="target">target</option>
              <option value="stop">stop</option>
              <option value="manual">manual</option>
            </select>
            <select value={pnlFilter} onChange={e => { setPnlFilter(e.target.value as 'all' | 'winners' | 'losers'); setPage(1); }}
              className="bg-gray-800 text-xs text-gray-300 px-2 py-1 rounded border border-gray-700">
              <option value="all">All P&L</option>
              <option value="winners">Winners only</option>
              <option value="losers">Losers only</option>
            </select>
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <span>Score</span>
              <input
                type="number"
                placeholder="min"
                value={scoreMinInput}
                onChange={e => { setScoreMinInput(e.target.value); setPage(1); }}
                className="w-14 bg-gray-800 text-xs text-gray-300 px-1.5 py-1 rounded border border-gray-700"
              />
              <span>-</span>
              <input
                type="number"
                placeholder="max"
                value={scoreMaxInput}
                onChange={e => { setScoreMaxInput(e.target.value); setPage(1); }}
                className="w-14 bg-gray-800 text-xs text-gray-300 px-1.5 py-1 rounded border border-gray-700"
              />
            </div>
          </div>
        </div>

        {/* Client-side filter disclosure: every filter above narrows already-loaded
            trades, so users see exactly which subset they're working with. */}
        <div className="text-[10px] text-gray-500 px-1">
          Showing {tableRows.length} of {trades.length} trades (client-side filter)
        </div>

        <div className="flex flex-wrap gap-3 text-[10px] text-gray-500 px-1">
          <span><span className="font-medium text-gray-400">MFE</span> — Max Favorable Excursion (peak unrealized profit)</span>
          <span><span className="font-medium text-gray-400">MAE</span> — Max Adverse Excursion (peak unrealized loss)</span>
          <span><span className="font-medium text-gray-400">Hold</span> — Time in trade</span>
          <span>
            <span className="font-medium text-gray-400">Criteria</span> —{' '}
            {CRITERIA_LABELS.map((c, i) => `${i + 1}. ${c.label}`).join('  ·  ')}
          </span>
        </div>

        {/* Class legend -- same descriptions as each ClassBadge's hover tooltip,
            surfaced here so the meaning of "Class" doesn't require hovering
            every row. Keep text in sync with CLASS_TIPS in ClassBadge.tsx. */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-gray-500 px-1">
          <span><ClassBadge signalClass="SMR" /> Full setup: HTF liquidity + SMT divergence + breaker + macro window. Highest probability.</span>
          <span><ClassBadge signalClass="MACRO_LIQ" /> Breaker + HTF liquidity + macro window. No SMT required. Half size.</span>
          <span><ClassBadge signalClass="MACRO_ONLY" /> Breaker + macro window only. No liquidity or SMT. Half size.</span>
          <span><ClassBadge signalClass="BREAKER_ONLY" /> Breaker alone. No macro, liquidity, or SMT. Logged only, no trade.</span>
          <span><ClassBadge signalClass="ORPHAN" /> Untracked Tradovate position found by orphan recovery. Manual review required.</span>
          <span><ClassBadge signalClass="ORPHAN_DUPLICATE" /> Phantom duplicate row from a flatten fill-matching edge case. Not a real position -- safe to ignore in P&L.</span>
        </div>

        <div className="overflow-y-auto max-h-[600px]">
          <TableWrapper>
            <Thead>
              <Th onClick={() => handleSort('open_time')}>Time <SortIndicator field="open_time" /></Th>
              <Th onClick={() => handleSort('instrument')}>Inst <SortIndicator field="instrument" /></Th>
              <Th onClick={() => handleSort('signal_class')}>Class <SortIndicator field="signal_class" /></Th>
              <Th onClick={() => handleSort('direction')}>Dir <SortIndicator field="direction" /></Th>
              <Th>Entry</Th>
              <Th>Exit</Th>
              <Th><CriteriaHeaderTooltip /></Th>
              <Th onClick={() => handleSort('quantity')}>Qty <SortIndicator field="quantity" /></Th>
              <Th onClick={() => handleSort('quality_score')}><ScoreHeaderTooltip /> <SortIndicator field="quality_score" /></Th>
              <Th onClick={() => handleSort('time_in_trade_seconds')}>Hold <SortIndicator field="time_in_trade_seconds" /></Th>
              <Th onClick={() => handleSort('mfe')} title="Max Favorable Excursion">MFE <SortIndicator field="mfe" /></Th>
              <Th onClick={() => handleSort('mae')} title="Max Adverse Excursion">MAE <SortIndicator field="mae" /></Th>
              <Th onClick={() => handleSort('exit_strategy')}>Exit Strategy <SortIndicator field="exit_strategy" /></Th>
              <Th>Order ID</Th>
              <Th onClick={() => handleSort('pnl')}>P&L <SortIndicator field="pnl" /></Th>
            </Thead>
            <Tbody>
              {loading ? (
                <TableLoadingRow colSpan={15} />
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan={15} className="px-3 py-6">
                    <EmptyState icon={<Inbox size={28} />} message="No trades found" sub="Closed and open trades will appear here." />
                  </td>
                </tr>
              ) : pageRows.map((t: any) => {
                const pnlVal = t.status === 'open' ? (t.unrealized_pnl ?? 0) : (t.net_pnl ?? t.pnl ?? 0);
                const exitCls =
                  t.exit_strategy === 'stop' ? 'bg-red-900 text-red-300' :
                  (t.exit_strategy === 'target' || t.exit_strategy === 'scaled_target') ? 'bg-green-900 text-green-300' :
                  t.exit_strategy === 'manual' ? 'bg-yellow-900 text-yellow-300' :
                  t.exit_strategy === 'scaled' ? 'bg-purple-900 text-purple-300' :
                  'bg-gray-700 text-gray-300';
                return (
                  <Tr key={t.id}>
                    <Td className="text-gray-400 text-xs">{formatTradeTime(t.open_time || t.created_at)}</Td>
                    <Td className="font-medium"><InstrumentCell symbol={t.instrument} /></Td>
                    <Td className="whitespace-nowrap"><ClassBadge signalClass={t.signal_class} /><EntryModeBadge mode={t.entry_mode} /></Td>
                    <Td><DirectionBadge dir={t.direction} /></Td>
                    <Td className="font-mono text-xs">{t.entry_price || '--'}</Td>
                    <Td className="font-mono text-xs">{t.exit_price || '--'}</Td>
                    <Td><CriteriaCell flags={t.criteria_flags} count={t.criteria_count} /></Td>
                    <Td>{t.quantity}</Td>
                    <Td><QualityScore score={t.quality_score} /></Td>
                    <Td className="font-mono text-xs text-gray-400">{formatDuration(t.time_in_trade_seconds)}</Td>
                    <Td className="font-mono text-xs text-green-400">${(t.mfe || 0).toFixed(0)}</Td>
                    <Td className="font-mono text-xs text-red-400">${(t.mae || 0).toFixed(0)}</Td>
                    <Td>
                      {t.exit_strategy ? (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${exitCls}`}>
                          {t.exit_strategy}
                        </span>
                      ) : (
                        <span className="text-gray-600">--</span>
                      )}
                    </Td>
                    <Td className="font-mono text-xs text-gray-400" title={t.stop_order_id || ''}>
                      {t.stop_order_id ? String(t.stop_order_id).slice(-8) : '--'}
                    </Td>
                    <Td className={`font-mono font-bold ${pnlColorClass(pnlVal)}`}>
                      {t.status === 'open' ? `$${pnlVal.toFixed(2)}` : `${pnlVal > 0 ? '+' : ''}$${pnlVal.toFixed(2)}`}
                      {t.status === 'open' && <span className="text-gray-500 text-xs ml-1">live</span>}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </TableWrapper>
        </div>

        {!showSystem && hiddenSystemCount > 0 && (
          <div className="text-xs text-gray-500 px-1">
            ⊘ {hiddenSystemCount} system trade{hiddenSystemCount === 1 ? '' : 's'} hidden (ORPHAN / ORPHAN_DUPLICATE) —{' '}
            <button
              onClick={() => { setShowSystem(true); setPage(1); }}
              className="text-blue-400 hover:text-blue-300 underline"
            >
              Show
            </button>
          </div>
        )}

        <PaginationBar
          totalRows={tableRows.length}
          startIdx={startIdx} endIdx={endIdx}
          page={page} pageCount={pageCount} setPage={setPage}
          pageSize={pageSize} setPageSize={setPageSize}
          noun="trades"
        />
      </div>
    </div>
  );
}

// ===== Misses Tab =====
function MissesTab() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try { const r = await getMissLog(); setRows(r.data || []); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);
  const { pageSize, setPageSize, page, setPage, pageCount, startIdx, endIdx } =
    usePagination(rows.length, 'signals_misses_page_size');
  const pageRows = rows.slice(startIdx, endIdx);
  return (
    <div className="space-y-3">
      <div className="p-3 bg-yellow-900/20 rounded-lg border border-yellow-900/30 flex items-center gap-2">
        <AlertTriangle size={14} className="text-yellow-400" />
        <span className="text-xs text-yellow-300">Signals that were evaluated but did not execute. Conditions column shows how many criteria were met. Review reason column to understand why each was blocked or skipped.</span>
      </div>
      <div className="overflow-y-auto max-h-[600px]">
        <TableWrapper>
          <Thead>
            <Th>Time</Th>
            <Th>Inst</Th>
            <Th>Class</Th>
            <Th><ScoreHeaderTooltip /></Th>
            <Th>Conditions</Th>
            <Th>Miss Reason</Th>
          </Thead>
          <Tbody>
            {loading ? (
              <TableLoadingRow colSpan={6} />
            ) : pageRows.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-6"><EmptyState icon={<Inbox size={28} />} message="No misses recorded" sub="Rejections will be logged here." /></td></tr>
            ) : pageRows.map((r: any) => {
              const reason = (r.notes && r.notes !== 'none') ? r.notes : (r.details?.reason || '');
              const { conditions, missReason } = parseRejectionReason(reason);
              return (
                <Tr key={r.id}>
                  <Td className="text-gray-400 text-xs">{formatTradeTime(r.created_at)}</Td>
                  <Td className="font-medium"><InstrumentCell symbol={r.instrument} /></Td>
                  <Td className="whitespace-nowrap"><ClassBadge signalClass={r.signal_class} /><EntryModeBadge mode={r.entry_mode} /></Td>
                  <Td><QualityScore score={typeof r.quality_score === 'number' ? r.quality_score : (parseInt(r.quality_score) || 0)} /></Td>
                  <Td className="font-mono text-xs">{conditions}</Td>
                  <Td className="text-xs text-gray-400 max-w-xs truncate" title={missReason}>{missReason || '—'}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </TableWrapper>
      </div>
      <PaginationBar
        totalRows={rows.length} startIdx={startIdx} endIdx={endIdx} page={page} pageCount={pageCount}
        setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} noun="misses"
      />
    </div>
  );
}

// Fix 8: parse the long reason blob into Conditions ("2/3") + the real miss reason text.
// 2026-07-21: this used to also derive an "Outcome" (stop/stdv2) from exit_type, which
// was a fabricated hypothetical result (assumed-always-optimistic STDV-2.0 hit, or a
// stop-out) -- never an observed outcome, since this system has no real price-history
// data to know what a missed signal would have done (see BACKTEST_HARNESS_REPORT.md
// section 5, item 2). That derivation has been removed; only the real miss reason
// text is surfaced now.
function parseRejectionReason(reason: string): { conditions: string; missReason: string } {
  const condMatch = reason.match(/Conditions:\s*(\d+\/\d+)/);
  const reasonMatch = reason.match(/^MISS:\s*([^|]+)/);
  return {
    conditions: condMatch?.[1] ?? '--',
    missReason: reasonMatch ? reasonMatch[1].trim() : reason,
  };
}

// ===== Deviations Tab =====
function DeviationsTab() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try { const r = await getDeviations(); setRows(r.data || []); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);
  const { pageSize, setPageSize, page, setPage, pageCount, startIdx, endIdx } =
    usePagination(rows.length, 'signals_deviations_page_size');
  const pageRows = rows.slice(startIdx, endIdx);
  return (
    <div className="space-y-3">
      <div className="p-3 bg-red-900/20 rounded-lg border border-red-900/30 flex items-center gap-2">
        <XCircle size={14} className="text-red-400" />
        <span className="text-xs text-red-300">Trades that closed at stop -- review if stop placement needs adjustment</span>
      </div>
      <div className="overflow-y-auto max-h-[600px]">
        <TableWrapper>
          <Thead>
            <Th>Time</Th>
            <Th>Inst</Th>
            <Th>Class</Th>
            <Th>Reason</Th>
          </Thead>
          <Tbody>
            {loading ? (
              <TableLoadingRow colSpan={4} />
            ) : pageRows.length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-6"><EmptyState icon={<Inbox size={28} />} message="No deviations recorded" sub="Stop-out and execution-fail trades will appear here." /></td></tr>
            ) : pageRows.map((r: any) => {
              // Fix 9: backend does not populate expected_exit / actual_exit; the
              // useful field is deviation_reason (falling back to notes).
              const reason = r.deviation_reason || (r.notes && r.notes !== 'none' ? r.notes : '') || '--';
              return (
                <Tr key={r.id}>
                  <Td className="text-gray-400 text-xs">{formatTradeTime(r.created_at)}</Td>
                  <Td className="font-medium"><InstrumentCell symbol={r.instrument} /></Td>
                  <Td className="whitespace-nowrap"><ClassBadge signalClass={r.signal_class} /><EntryModeBadge mode={r.entry_mode} /></Td>
                  <Td className="text-xs text-gray-300 max-w-md">{reason}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </TableWrapper>
      </div>
      <PaginationBar
        totalRows={rows.length} startIdx={startIdx} endIdx={endIdx} page={page} pageCount={pageCount}
        setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} noun="deviations"
      />
    </div>
  );
}

// ===== Risk Events Tab =====
// Friendly labels for risk_log event types (raw value shown in tooltip).
export const RISK_EVENT_LABELS: Record<string, string> = {
  trade_approved: 'Approved',
  trade_blocked: 'Blocked',
  kill_switch_activated: 'Kill Switch',
  mtf_trend_conflict_block: 'MTF Trend Block',
  mtf_trend_gate_eval: 'MTF Gate Eval',
  shadow_block: 'Shadow Block',
  second_chance_armed: 'Second Chance Armed',
  second_chance_position_open: 'Second Chance Open',
  sizing_decision: 'Sizing',
  counter_bias_decision: 'Counter-Bias',
  tp1_rr_gate_long_block: 'TP1 R:R Gate',
};

const RISK_EVENT_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Events' },
  { value: 'trade_approved', label: 'Approved' },
  { value: 'trade_blocked', label: 'Blocked' },
  { value: 'kill_switch_activated', label: 'Kill Switch' },
  { value: 'mtf_trend_conflict_block', label: 'MTF Trend Block' },
  { value: 'mtf_trend_gate_eval', label: 'MTF Gate Eval' },
  { value: 'shadow_block', label: 'Shadow Block' },
  { value: 'second_chance_armed', label: 'Second Chance Armed' },
  { value: 'sizing_decision', label: 'Sizing' },
];

const RISK_CLASS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Classes' },
  { value: 'SMR', label: 'SMR' },
  { value: 'MACRO_LIQ', label: 'MACRO_LIQ' },
  { value: 'MACRO_ONLY', label: 'MACRO_ONLY' },
  { value: 'BREAKER_ONLY', label: 'BREAKER_ONLY' },
  { value: 'TREND_CONT', label: 'TREND_CONT' },
  { value: 'SECOND_CHANCE', label: 'SECOND_CHANCE' },
  { value: 'BIAS_SNIPE', label: 'BIAS_SNIPE (shadow)' },
  { value: 'DAILY_TREND', label: 'DAILY_TREND (shadow)' },
];

// MTF gate-eval rows fire on every routed signal; aligned/neutral/missing
// outcomes are pure audit noise, so they're hidden unless the user filters
// to MTF Gate Eval explicitly or unticks the box.
const MTF_EVAL_NOISE_REASONS = new Set(['mtf_aligned', 'mtf_neutral', 'mtf_bias_fields_missing']);

function RiskEventsTab() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState<string>('all');
  const [classFilter, setClassFilter] = useState<string>('all');
  const [hideEvalNoise, setHideEvalNoise] = useState<boolean>(true);
  useEffect(() => {
    let first = true;
    const load = async () => {
      if (first) setLoading(true);
      try {
        const r = await getRiskLog({
          limit: 300,
          ...(eventFilter !== 'all' ? { event: eventFilter } : {}),
          ...(classFilter !== 'all' ? { signal_class: classFilter } : {}),
        });
        setRows(r.data || []);
      }
      catch (e) { console.error(e); }
      finally { if (first) { setLoading(false); first = false; } }
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [eventFilter, classFilter]);
  const visibleRows = rows.filter((r: any) => {
    if (hideEvalNoise && eventFilter !== 'mtf_trend_gate_eval' &&
        r.event === 'mtf_trend_gate_eval' && MTF_EVAL_NOISE_REASONS.has(r.details?.reason)) {
      return false;
    }
    return true;
  });
  const { pageSize, setPageSize, page, setPage, pageCount, startIdx, endIdx } =
    usePagination(visibleRows.length, 'signals_risk_page_size');
  const pageRows = visibleRows.slice(startIdx, endIdx);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end flex-wrap gap-2">
        <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer" title="MTF gate evals fire on every routed signal; aligned/neutral outcomes are noise. Conflicts and blocks are always shown.">
          <input type="checkbox" checked={hideEvalNoise} onChange={e => setHideEvalNoise(e.target.checked)} />
          Hide MTF eval noise
        </label>
        <select value={eventFilter} onChange={e => setEventFilter(e.target.value)}
          className="bg-gray-800 text-xs text-gray-300 px-2 py-1 rounded border border-gray-700">
          {RISK_EVENT_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={classFilter} onChange={e => setClassFilter(e.target.value)}
          className="bg-gray-800 text-xs text-gray-300 px-2 py-1 rounded border border-gray-700">
          {RISK_CLASS_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div className="overflow-y-auto max-h-[600px]">
        <TableWrapper>
          <Thead>
            <Th>Time</Th>
            <Th>Inst</Th>
            <Th>Class</Th>
            <Th>Event</Th>
            <Th><CriteriaHeaderTooltip /></Th>
            <Th>Bias / Size</Th>
            <Th>Status</Th>
            <Th>Reason</Th>
          </Thead>
          <Tbody>
            {loading ? (
              <TableLoadingRow colSpan={8} />
            ) : pageRows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-6"><EmptyState icon={<Inbox size={28} />} message="No risk events" sub="Passes and blocks will appear here." /></td></tr>
            ) : pageRows.map((r: any) => {
              const d = r.details || {};
              const reasons: string[] = d.reasons || [];
              // Phase B (7/10+) criteria-gate events store the reason under
              // block_reason, not reason/reasons -- old code left these blank.
              let reasonText = reasons.length > 0 ? reasons.join('; ') : (d.reason || d.block_reason || '');
              // MTF gate rows: surface daily-bias stance vs signal direction inline.
              if ((r.event === 'mtf_trend_gate_eval' || r.event === 'mtf_trend_conflict_block') && (d.daily_bias || d.new_direction)) {
                reasonText = `${reasonText} · bias ${d.daily_bias ?? '?'} vs ${d.new_direction ?? '?'}`;
              }
              const isPassed = /^All risk checks passed/i.test(reasonText);
              const blocked = !!r.blocked;
              const warnEvent = r.event === 'kill_switch_activated' || r.event === 'trade_blocked';
              // criteria_flags on risk_log uses in_macro_window; CriteriaCell
              // expects in_macro. Remap keys rather than touch the shared cell.
              const rawFlags = d.criteria_flags as Record<string, boolean> | undefined;
              const flags = rawFlags ? {
                htf_liquidity_taken: !!rawFlags.htf_liquidity_taken,
                smt_confirmed: !!rawFlags.smt_confirmed,
                breaker_formed: !!rawFlags.breaker_formed,
                in_macro: !!rawFlags.in_macro_window,
              } : null;
              const criteriaCount = typeof d.criteria_count === 'number' ? d.criteria_count : null;
              const biasAligned = d.bias_aligned;
              const contracts = d.contracts;
              return (
                <Tr key={r.id}>
                  <Td className="text-gray-400 text-xs">{formatTradeTime(r.created_at)}</Td>
                  <Td className="font-medium"><InstrumentCell symbol={r.instrument || ''} /></Td>
                  <Td className="whitespace-nowrap"><ClassBadge signalClass={r.signal_class} /></Td>
                  <Td className="text-xs"><span title={r.event} className="cursor-help">{RISK_EVENT_LABELS[r.event] || r.event}</span></Td>
                  <Td><CriteriaCell flags={flags} count={criteriaCount} /></Td>
                  <Td className="text-xs whitespace-nowrap">
                    {biasAligned == null ? <span className="text-gray-600">--</span> : (
                      <span className={biasAligned ? 'text-green-400' : 'text-amber-300'}>
                        {biasAligned ? 'Aligned' : 'Counter-bias'}{typeof contracts === 'number' ? ` · ${contracts}c` : ''}
                      </span>
                    )}
                  </Td>
                  <Td>
                    {blocked
                      ? <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-900 text-red-300">Blocked</span>
                      : <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-900 text-green-300">Passed</span>}
                  </Td>
                  <Td className="text-xs max-w-md">
                    {isPassed && !blocked && !warnEvent ? (
                      <span className="inline-flex items-center text-green-400"><CheckCircle2 size={14} /></span>
                    ) : (
                      <span className={blocked || warnEvent ? 'text-red-400' : 'text-amber-300'}>{reasonText || '--'}</span>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </TableWrapper>
      </div>
      <PaginationBar
        totalRows={visibleRows.length} startIdx={startIdx} endIdx={endIdx} page={page} pageCount={pageCount}
        setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} noun="events"
      />
    </div>
  );
}

// Fix 4: tri-state decision pill (triggered / skipped / rejected).
function DecisionPill({ decision }: { decision: 'triggered' | 'skipped' | 'rejected' }) {
  const cls =
    decision === 'triggered' ? 'bg-blue-900 text-blue-300' :
    decision === 'skipped' ? 'bg-yellow-900 text-yellow-300' :
    'bg-gray-700 text-gray-300';
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{decision}</span>;
}

// Fix 5: ALERT column. Backend exposes only one combined `alert_source` field
// today. For the Pine-script identity value, show a friendlier display name
// with the raw value in a hover tooltip. For alert-type values (PAC_breaker,
// HTF_liquidity_swept, etc.) show as-is.
// NOTE -- Backend task: split source into pine_script_name + alert_type fields.
function AlertCell({ source }: { source?: string | null }) {
  if (!source) return <span className="text-gray-600">--</span>;
  const isPineScript = /^breaker_trading_v\d+$/i.test(source);
  if (isPineScript) {
    const display = source.replace(/^breaker_trading_v/i, 'Breaker v');
    return (
      <span className="relative group cursor-default text-gray-300">
        {display}
        <span className="hidden group-hover:block absolute z-20 bottom-full left-0 mb-1 bg-gray-800 text-xs text-white px-2 py-1 rounded whitespace-nowrap shadow-lg pointer-events-none">
          {source}
        </span>
      </span>
    );
  }
  return <span className="text-gray-300">{source}</span>;
}

// Chart 9: signals throughput strip (last 24h, stacked by decision).
function SignalsThroughput({ signals, decisionOf }: { signals: any[]; decisionOf: (s: any) => 'triggered' | 'skipped' | 'rejected' }) {
  const data = useMemo(() => {
    const now = Date.now();
    const hours = Array.from({ length: 24 }, (_, i) => {
      const h = new Date(now - (23 - i) * 3600000);
      // Fix (2026-07-15): label was browser-local time (h.getHours()) while
      // the rest of the app displays ET. Bucket math itself was already
      // timezone-agnostic (relative offsets from `now`), only the label was
      // wrong -- format it in ET to match.
      const etHour = h.toLocaleString('en-US', { hour: 'numeric', hour12: false, timeZone: 'America/New_York' });
      return {
        idx: i,
        label: `${etHour}:00`,
        triggered: 0,
        skipped: 0,
        rejected: 0,
      };
    });
    signals.forEach(s => {
      const ts = new Date(s.created_at).getTime();
      const age = now - ts;
      if (age > 24 * 3600000 || age < 0) return;
      const hIdx = 23 - Math.floor(age / 3600000);
      if (hIdx < 0 || hIdx > 23) return;
      const d = decisionOf(s);
      hours[hIdx][d]++;
    });
    return hours;
  }, [signals, decisionOf]);

  const total = data.reduce((sum, h) => sum + h.triggered + h.skipped + h.rejected, 0);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-500 uppercase tracking-wider">Throughput (last 24h)</span>
        <div className="flex items-center gap-3 text-[10px] text-gray-500">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400" />Triggered</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400" />Skipped</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-500" />Rejected</span>
          <span className="ml-2">{total} signals</span>
        </div>
      </div>
      {total === 0 ? (
        <div className="text-xs text-gray-600 py-2">No signals in the last 24 hours</div>
      ) : (
        <ResponsiveContainer width="100%" height={60}>
          <BarChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: 4 }} barSize={8}>
            <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6b7280' }} interval={3} />
            <RTooltip
              contentStyle={{ background: '#1f2937', border: '1px solid #374151', fontSize: 12 }}
            />
            <Bar dataKey="triggered" stackId="a" fill="#60a5fa" />
            <Bar dataKey="skipped" stackId="a" fill="#facc15" />
            <Bar dataKey="rejected" stackId="a" fill="#4b5563" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
